import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ResultSearch from "../../components/VMRS/ExploreArchive/ResultSearch";
import SatelliteSearchPanel from "../../components/VMRS/ExploreArchive/SatelliteSearchPanel";
import { MapContext } from "../../components/VMRS/MapAdapter/core/MapContext";
import { UniversalMap } from "../../components/VMRS/MapAdapter/UniversalMap";
import { ETypeImage } from "../../enums";
import { showError } from "../../helpers/toast";
import ContentLayoutManagement from "../../layouts/ContentLayoutManagement";
import HeaderContent from "../../layouts/HeaderContent";
import
{
   getImageTypesAsync,
   searchAsync,
   getFormSearchByImageTypeAsync,
} from "../../store/reducers/mapTaskingSlice";
import
{
   resetProviderData,
   setDataImages,
   setDataImagesProvider,
   setLoadingProvider,
} from "../../store/reducers/searchSlice";
import { RootState } from "../../store/store";
import { Spin } from "antd";
import
{
   clearAOIFeatures,
   setDrawingAOIMode,
   toggleFeatureVisibility,
} from "../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import
{
   clearAllLayers,
   removeLayer,
} from "../../components/VMRS/MapAdapter/store/slices/layerSlice";

import { clearFeatures as clearDrawFeatures, setDrawingMode } from "../../components/VMRS/MapAdapter/store/slices/drawSlice";
import { clearSegments as clearMeasureSegments, setMeasureMode } from "../../components/VMRS/MapAdapter/store/slices/measureSlice";

const ExploreArchivePage = () =>
{
   // const [mapInstance, setMapInstance] = useState<any>(null);

   const [loading, setLoading] = useState(false);
   const [engine, setEngine] = useState<any>(null);

   const [startDate, setStartDate] = useState<any>(dayjs().subtract(1, "month"));
   const [endDate, setEndDate] = useState<any>(dayjs());

   const [imageType, setImageType] = useState(ETypeImage.OPTICAL);

   const [data, setData] = useState<any[]>([]);
   const [showResultSearch, setShowResultSearch] = useState(false);
   const [showBackToResultSearch, setShowBackToResultSearch] = useState(false);

   const [providers, setProviders] = useState<any>([]);
   const [providerCode, setProviderCode] = useState(undefined);
   const [providerId, setProviderId] = useState(undefined);

   const providerCodeRef = useRef<string | undefined>(undefined);
   useEffect(() =>
   {
      providerCodeRef.current = providerCode;
   }, [providerCode]);

   const [imageTypes, setImageTypes] = useState<any>([]);

   const [formConfig, setFormConfig] = useState<any>(null);
   const [formData, setFormData] = useState<Record<string, any>>({});

   const dispatch = useDispatch();
   const { t } = useTranslation();
   const allAOI = useSelector((state: RootState) => state.aoi.features);
   const activeFeatureId: any = useSelector(
      (state: RootState) => state.aoi.activeFeatureId,
   );

   let searchState = useSelector((state: RootState) => state.search);

   let page = useSelector((state: RootState) => state.search.page);

   const layers = useSelector((state: RootState) => state.layer.layers);

   const handleSearch = () =>
   {
      setShowResultSearch(true);
      setShowBackToResultSearch(true);

      if (layers && layers.length > 0)
      {
         const targets = layers.filter(
            (layer) =>
               layer.id.startsWith("image-") || layer.id.startsWith("footerprint-"),
         );

         targets.forEach((layer) =>
         {
            dispatch(removeLayer(layer.id));
         });
      }

      if (!allAOI?.filter((aoi) => aoi.id === activeFeatureId)?.[0]?.visible)
      {
         dispatch(toggleFeatureVisibility({ id: activeFeatureId }));
      }

      searchAllProviders();
   };

   const searchAllProviders = async () =>
   {
      setData([]);
      dispatch(resetProviderData());
      if (providers?.length > 0)
      {
         const searchPromises = providers?.map(async (provider: any) =>
         {
            dispatch(setLoadingProvider({ code: provider.code, value: true }));

            // Destructure UI-only/computed fields using hyphenated key for resolution-swith
            const { "resolution-swith": resolutionSwith, ...otherParams } =
               formData as any;

            // Compute resolution/off_nadir based on resolution-swith
            let resolutionField: any = {};
            if (resolutionSwith === "gsd")
            {
               resolutionField = { gsd: formData?.gsd };
            } else if (resolutionSwith === "off_nadir")
            {
               resolutionField = { off_nadir: formData?.off_nadir };
            }

            const pay = {
               ...otherParams,
               aois: [allAOI?.[0]?.geometry],
               ...resolutionField,
               page_number: page,
               page_size: 50,
               provider_code: provider.code,
               image_type: imageType,
            };

            // Convert any date string fields to timestamps
            // Object.keys(pay).forEach((key) =>
            // {
            //    const value = pay[key];
            //    if (typeof value === "string" && value.length > 0)
            //    {
            //       const d = dayjs(value);
            //       if (d.isValid())
            //       {
            //          pay[key] = d.valueOf();
            //       }
            //    }
            // });

            Object.keys(pay).forEach((key) =>
            {
               const value = pay[key];

               if (typeof value === "string" && value.length > 0)
               {
                  let d = dayjs(value);
                  if (!d.isValid()) return;

                  const isToday = d.isSame(dayjs(), "day");

                  const isStart =
                     key.toLowerCase().includes("start") ||
                     key.toLowerCase().includes("from");
                  const isEnd =
                     key.toLowerCase().includes("end") ||
                     key.toLowerCase().includes("to");

                  if (isStart)
                  {
                     d = d.startOf("day"); // 00:00:00
                  }

                  if (isEnd)
                  {
                     if (isToday)
                     {
                        d = dayjs(); // giờ hiện tại
                     } else
                     {
                        d = d.endOf("day"); // 23:59:59
                     }
                  }

                  pay[key] = d.valueOf();
               }
            });

            try
            {
               // @ts-ignore
               const resSearch = await dispatch(searchAsync(pay)).unwrap();
               if (resSearch?.status !== 200)
               {
                  showError(t(resSearch?.response?.data?.message ?? "Error"));
               }

               console.log(resSearch, "resSearch");

               dispatch(
                  setDataImagesProvider({
                     code: provider.code,
                     data: resSearch?.data?.data,
                     page: resSearch?.data?.data?.total_record ? page + 1 : page,
                     nextLink: !resSearch?.data?.data?.total_record
                        ? resSearch?.data?.data?.links?.filter(
                           (itemF: any) => itemF?.rel === "next",
                        )?.[0]?.href
                        : undefined,
                     firstLink: !resSearch?.data?.data?.total_record
                        ? resSearch?.data?.data?.links?.filter(
                           (itemF: any) => itemF?.rel === "first",
                        )?.[0]?.href
                        : undefined,
                     total:
                        resSearch?.data?.data?.total_record ||
                        resSearch?.data?.data?.length,
                  }),
               );

               if (provider.code === providerCodeRef.current)
               {
                  dispatch(
                     setDataImages({
                        code: provider.code,
                        data: resSearch?.data?.data ?? [],
                     }),
                  );

                  setData(resSearch?.data?.data ?? []);
               }

               dispatch(setLoadingProvider({ code: provider.code, value: false }));

               return {
                  code: provider.code,
                  total:
                     resSearch?.data?.data?.total_record ||
                     resSearch?.data?.data?.length,
                  listImages: resSearch?.data?.data || [],
                  [`loading_${provider.code}`]: false,
               };
            } catch (error)
            {
               dispatch(setLoadingProvider({ code: provider.code, value: false }));
               return {
                  code: provider.code,
                  total: 0,
                  listImages: [],
                  [`loading_${provider.code}`]: false,
               };
            }
         });
         const results = await Promise.all(searchPromises);
      }
   };

   const getDataInmagsByNextLink = async (link: any) =>
   {
      if (!link) return;

      const targetProvider = providerCode;

      try
      {
         const res = await axios.get(link);

         // @ts-ignore
         const baseForTarget = searchState[`data_images_${targetProvider}`] ?? [];
         const newArr = [...baseForTarget, ...(res?.data?.data || [])];
         const links = res?.data?.data?.links;
         dispatch(
            setDataImages({
               code: targetProvider,
               data: newArr,
            }),
         );

         dispatch(
            setDataImagesProvider({
               id: targetProvider,
               data: newArr,
               page: 1,
               nextLink: links?.find((item: any) => item?.rel === "next")?.href,
               firstLink: links?.find((item: any) => item?.rel === "first")?.href,
               total: newArr?.length,
            }),
         );

         if (providerCodeRef.current === targetProvider)
         {
            setData(newArr);
         }
      } catch (error)
      {
      } finally
      {
         dispatch(setLoadingProvider({ code: targetProvider, value: false }));
      }
   };

   const search = async (props?: any) =>
   {
      const { "resolution-swith": resolutionSwith, ...otherParams } =
         formData as any;

      // Compute resolution/off_nadir based on resolution-swith
      let resolutionField: any = {};
      if (resolutionSwith === "gsd")
      {
         resolutionField = { gsd: formData?.gsd };
      } else if (resolutionSwith === "off_nadir")
      {
         resolutionField = { off_nadir: formData?.off_nadir };
      }

      const pay = {
         ...otherParams,
         aois: [
            allAOI?.filter((aoi) => aoi.id === activeFeatureId)?.[0]?.geometry,
         ],
         ...resolutionField,
         page_number: page,
         page_size: 50,
         provider_code: providerCode,
         image_type: imageType,
      };

      // Convert any date string fields to timestamps
      // Object.keys(pay).forEach((key) =>
      // {
      //    const value = pay[key];
      //    if (typeof value === "string" && value.length > 0)
      //    {
      //       const d = dayjs(value);
      //       if (d.isValid())
      //       {
      //          pay[key] = d.valueOf();
      //       }
      //    }
      // });

      Object.keys(pay).forEach((key) =>
      {
         const value = pay[key];

         if (typeof value === "string" && value.length > 0)
         {
            let d = dayjs(value);
            if (!d.isValid()) return;

            const isToday = d.isSame(dayjs(), "day");

            const isStart =
               key.toLowerCase().includes("start") ||
               key.toLowerCase().includes("from");
            const isEnd =
               key.toLowerCase().includes("end") || key.toLowerCase().includes("to");

            if (isStart)
            {
               d = d.startOf("day"); // 00:00:00
            }

            if (isEnd)
            {
               if (isToday)
               {
                  d = dayjs(); // giờ hiện tại
               } else
               {
                  d = d.endOf("day"); // 23:59:59
               }
            }

            pay[key] = d.valueOf();
         }
      });

      const targetProvider = providerCode;

      try
      {
         // @ts-ignore
         let resSearch = await dispatch(searchAsync(pay)).unwrap();

         if (resSearch?.status !== 200)
         {
            showError(t(resSearch?.response?.data?.message ?? "Error"));
         }
         // @ts-ignore
         const currentPage = searchState[`page_${targetProvider}`] || 1;
         // @ts-ignore
         const baseForTarget = searchState[`data_images_${targetProvider}`] ?? [];
         const newDataForTarget = baseForTarget.concat(
            resSearch?.data?.data ?? [],
         );

         dispatch(
            setDataImagesProvider({
               code: targetProvider,
               data: newDataForTarget,
               page: currentPage + 1,
               nextLink: undefined,
               firstLink: undefined,
               total: resSearch?.data?.data?.total_record,
            }),
         );

         if (providerCodeRef.current === targetProvider)
         {
            dispatch(
               setDataImages({
                  code: targetProvider,
                  data: newDataForTarget,
               }),
            );
            setData(newDataForTarget);
         }
      } catch (error)
      {
      } finally
      {
         dispatch(setLoadingProvider({ code: targetProvider, value: false }));
      }

      // try
      // {
      //    // @ts-ignore
      //    let resSearch = await dispatch(searchAsync(pay)).unwrap();

      //    if (resSearch?.status !== 200)
      //    {
      //       showError(t(resSearch?.response?.data?.message ?? "Error"));
      //    }

      //    dispatch(
      //       setDataImagesProvider({
      //          code: providerCode,
      //          data: data.concat(resSearch?.data?.data),
      //          // @ts-ignore
      //          page: searchState[`page_${providerCode}`] + 1,
      //          nextLink: undefined,
      //          firstLink: undefined,
      //          total: resSearch?.data?.data?.total_record,
      //       }),
      //    );

      //    setData(data.concat(resSearch?.data?.data ?? []));
      // } catch (error)
      // {
      // } finally
      // {
      //    dispatch(setLoadingProvider({ code: providerCode, value: false }));
      // }
   };

   const handleScroll = (e: any) =>
   {
      const { scrollTop, clientHeight, scrollHeight } = e.target;

      // Kiểm tra nếu đã cuộn xuống cuối danh sách

      if (scrollTop + clientHeight >= scrollHeight && scrollTop > 0)
      {
         // @ts-ignore
         let nextLink = searchState[`next_link_${providerCode}`];
         // @ts-ignore
         let firstLink = searchState[`first_link_${providerCode}`];

         if (nextLink && nextLink !== "" && nextLink !== firstLink)
         {
            dispatch(setLoadingProvider({ code: providerCode, value: true }));
            getDataInmagsByNextLink(nextLink);
         } else
         {
            // @ts-ignore
            let total = searchState[`total_images_${providerCode}`];

            if (total > data?.length)
            {
               dispatch(setLoadingProvider({ code: providerCode, value: true }));

               search();
            }
         }
      }
   };

   const handleGetImageTypes = async () =>
   {
      try
      {
         setLoading(true);
         // @ts-ignore
         let res = await dispatch(getImageTypesAsync()).unwrap();

         if (res?.status === 200)
         {
            let dataImageTypes = res?.data?.data ?? [];

            setImageTypes(dataImageTypes);

            // Find first image type with providers
            const imageTypeWithProviders = dataImageTypes.find(
               (item: any) => item?.providers && item?.providers?.length > 0,
            );

            if (imageTypeWithProviders)
            {
               // Set the image type
               setImageType(imageTypeWithProviders.value);
               setProviders(imageTypeWithProviders.providers);
               setProviderCode(imageTypeWithProviders.providers[0]?.code);
               setProviderId(imageTypeWithProviders.providers[0]?.id);
            }
         }
      } catch (error)
      {
         console.error(error);
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      handleGetImageTypes();
   }, []);

   const handleResetMap = () =>
   {
      dispatch(clearAOIFeatures());
      dispatch(clearDrawFeatures());
      dispatch(clearMeasureSegments());
      dispatch(clearAllLayers());
      dispatch(setDrawingAOIMode("NONE"));
      dispatch(setDrawingMode("NONE"));
      dispatch(setMeasureMode("NONE"));
   };

   useEffect(() =>
   {
      handleResetMap();

      return () =>
      {
         handleResetMap();
      };
   }, []);

   // Load form configuration when image type changes
   useEffect(() =>
   {
      const loadFormConfig = async () =>
      {
         if (!imageTypes || imageTypes.length === 0)
         {
            return;
         }
         const currentImageType = imageTypes.find(
            (item: any) => item.value === imageType,
         );
         if (!currentImageType?.key)
         {
            console.warn("No form config key found for image type:", imageType);
            return;
         }
         try
         {
            setLoading(true);
            // @ts-ignore
            const formRes = await dispatch(
               // @ts-ignore
               getFormSearchByImageTypeAsync({ key: currentImageType.key }),
            ).unwrap();

            if (formRes?.status === 200)
            {
               setFormConfig(formRes?.data?.data);
               const defaults: Record<string, any> = {};

               // Extract defaults from ui_schema.options FIRST (including range defaults) - higher priority
               if (formRes?.data?.data?.ui_schema?.ui?.options)
               {
                  Object.entries(formRes.data.data.ui_schema.ui.options).forEach(
                     ([key, option]: [string, any]) =>
                     {
                        // Support both single default and range defaults (defaultMin/defaultMax)
                        if (
                           option.defaultMin !== undefined &&
                           option.defaultMax !== undefined
                        )
                        {
                           defaults[key] = [option.defaultMin, option.defaultMax];
                        } else if (option.defaultMin !== undefined)
                        {
                           defaults[key] = [option.defaultMin, option.defaultMin];
                        } else if (option.defaultMax !== undefined)
                        {
                           defaults[key] = [option.defaultMax, option.defaultMax];
                        } else if (option.default !== undefined)
                        {
                           defaults[key] = option.default;
                        }
                     },
                  );
               }

               // Then fill missing defaults from form_schema.properties (fallback)
               if (formRes?.data?.data?.form_schema?.schema?.properties)
               {
                  Object.entries(
                     formRes.data.data.form_schema.schema.properties,
                  ).forEach(([key, prop]: [string, any]) =>
                  {
                     if (prop.default !== undefined && defaults[key] === undefined)
                     {
                        defaults[key] = prop.default;
                     }
                  });
               }

               // Handle dependencies: filter out fields whose depField condition is not met
               if (formRes?.data?.data?.ui_schema?.ui?.options)
               {
                  Object.entries(formRes.data.data.ui_schema.ui.options).forEach(
                     ([key, option]: [string, any]) =>
                     {
                        // if (option.depField || option.depends_on)
                        // {
                        //    let depField: string;
                        //    let depValue: string | number | boolean;

                        //    if (option.depends_on)
                        //    {
                        //       depField = option.depends_on.field;
                        //       depValue = option.depends_on.value;
                        //    } else if (option.depField)
                        //    {
                        //       depField = option.depField;
                        //       // Legacy: field is shown when controller equals this field's key
                        //       depValue = key;
                        //    }

                        //    const controllerValue = defaults[depField];
                        //    // If controller is defined and condition not met, remove this field
                        //    if (
                        //       controllerValue !== undefined &&
                        //       controllerValue !== depValue
                        //    )
                        //    {
                        //       delete defaults[key];
                        //    }
                        // }

                        if (option.depField || option.depends_on)
                        {
                           const depField = option.depends_on?.field || option.depField;
                           const depValue = option.depends_on?.value ?? key;

                           if (!depField) return;

                           const controllerValue = defaults[depField];

                           if (
                              controllerValue !== undefined &&
                              controllerValue !== depValue
                           )
                           {
                              delete defaults[key];
                           }
                        }
                     },
                  );
               }

               console.log("Extracted defaults:", defaults);

               if (startDate && defaults.date_from === undefined)
               {
                  defaults.date_from = startDate.toISOString();
               }
               if (endDate && defaults.date_to === undefined)
               {
                  defaults.date_to = endDate.toISOString();
               }

               setFormData(defaults);
            }
         } catch (error)
         {
            console.error(
               "Failed to fetch form config for image type:",
               imageType,
               error,
            );
         } finally
         {
            setLoading(false);
         }
      };

      loadFormConfig();
   }, [imageType, imageTypes, dispatch, startDate, endDate]);

   // Handle resolution-swith toggle: dynamically set gsd or off_nadir based on UI option defaults
   useEffect(() =>
   {
      if (!formConfig) return;

      const resolutionSwith = formData["resolution-swith"];
      if (!resolutionSwith) return;

      const options = formConfig.ui_schema?.ui?.options || {};

      setFormData((prev) =>
      {
         const newData = { ...prev };

         // Always remove both fields first
         delete newData.gsd;
         delete newData.off_nadir;

         if (resolutionSwith === "gsd")
         {
            let gsdDefault: any;
            if (options.gsd?.default !== undefined)
            {
               gsdDefault = options.gsd.default;
            } else if (
               options.gsd?.defaultMin !== undefined ||
               options.gsd?.defaultMax !== undefined
            )
            {
               const min = options.gsd.defaultMin ?? options.gsd.default ?? 0;
               const max = options.gsd.defaultMax ?? options.gsd.default ?? 100;
               gsdDefault = [min, max];
            }

            if (gsdDefault !== undefined)
            {
               newData.gsd = gsdDefault;
            }
         } else if (resolutionSwith === "off_nadir")
         {
            let offNadirDefault: any;
            if (options.off_nadir?.default !== undefined)
            {
               offNadirDefault = options.off_nadir.default;
            } else if (
               options.off_nadir?.defaultMin !== undefined ||
               options.off_nadir?.defaultMax !== undefined
            )
            {
               const min =
                  options.off_nadir.defaultMin ?? options.off_nadir.default ?? 0;
               const max =
                  options.off_nadir.defaultMax ?? options.off_nadir.default ?? 100;
               offNadirDefault = [min, max];
            }

            if (offNadirDefault !== undefined)
            {
               newData.off_nadir = offNadirDefault;
            }
         }

         return newData;
      });
   }, [formData["resolution-swith"], formConfig]);

   useEffect(() =>
   {
      if (!providerCode || !layers || layers.length === 0) return;

      const targets = layers.filter(
         (layer) =>
            layer.id.startsWith("image-") || layer.id.startsWith("footerprint-"),
      );

      targets.forEach((layer) =>
      {
         dispatch(removeLayer(layer.id));
      });
   }, [providerCode]);

   return (
      <MapContext.Provider
         value={{
            engine: engine,
            pluginManager: null,
            mapType: "maplibre",
            mapInstance: engine?.map,
         }}
      >
         <ContentLayoutManagement
            header={
               <HeaderContent breadcrumb={[{ title: t("Explore Archive") }]} />
            }
         >
            <div className="h-full flex bg-[var(--neutral-1000)] text-white">
               {/* LEFT PANEL */}
               <div className="w-5/12 flex flex-col border-r border-[#26324a]">
                  <Spin spinning={loading}>
                     {!showResultSearch && (
                        <SatelliteSearchPanel
                           imageType={imageType}
                           setImageType={setImageType}
                           handleSearch={handleSearch}
                           showBackToResultSearch={showBackToResultSearch}
                           setShowResultSearch={setShowResultSearch}
                           providers={providers}
                           imageTypes={imageTypes}
                           formConfig={formConfig}
                           formData={formData}
                           setFormData={setFormData}
                           setProviders={setProviders}
                           setProviderCode={setProviderCode}
                           setProviderId={setProviderId}
                        />
                     )}

                     {showResultSearch && (
                        <>
                           <ResultSearch
                              providerCode={providerCode}
                              setProviderCode={setProviderCode}
                              providers={providers}
                              data={data}
                              setShowResultSearch={setShowResultSearch}
                              setData={setData}
                              mapInstance={engine?.map}
                              handleScroll={handleScroll}
                              providerId={providerId}
                              loading={loading}
                              setLoading={setLoading}
                           />
                        </>
                     )}
                  </Spin>
               </div>

               {/* RIGHT MAP */}
               <div className="w-7/12 flex-1 relative">
                  <UniversalMap
                     mapType="maplibre"
                     showEngine={false}
                     showDraw={false}
                     showDrawAOI={!showResultSearch}
                     showMeasure={false}
                     view={{
                        center: [108.23653393802581, 16.1039225743402],
                        zoom: 5,
                     }}
                     showBaseMap={true}
                     showMapControl={true}
                     onMapReady={setEngine}
                     showSearchOSM={false}
                     allowMoveActive={!showResultSearch}
                  />
               </div>
            </div>
         </ContentLayoutManagement>
      </MapContext.Provider>
   );
};

export default ExploreArchivePage;
